import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const ToastContext = createContext(null);

const DEFAULT_DURATION = 4000;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timeouts = useRef(new Map());

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timeouts.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timeouts.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (toast) => {
      const id = toast.id || `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const entry = {
        id,
        variant: toast.variant || 'info',
        title: toast.title,
        message: toast.message,
        duration: toast.duration ?? DEFAULT_DURATION,
      };
      setToasts((prev) => [...prev, entry]);
      if (entry.duration > 0) {
        const handle = setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
          timeouts.current.delete(id);
        }, entry.duration);
        timeouts.current.set(id, handle);
      }
      return id;
    },
    []
  );

  useEffect(() => {
    const timeoutMap = timeouts.current;
    return () => {
      timeoutMap.forEach((handle) => clearTimeout(handle));
      timeoutMap.clear();
    };
  }, []);

  const api = useMemo(
    () => ({
      toasts,
      push,
      remove,
      success: (message, options = {}) => push({ ...options, message, variant: 'success' }),
      error: (message, options = {}) => push({ ...options, message, variant: 'error' }),
      info: (message, options = {}) => push({ ...options, message, variant: 'info' }),
      warning: (message, options = {}) => push({ ...options, message, variant: 'warning' }),
    }),
    [toasts, push, remove]
  );

  return <ToastContext.Provider value={api}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
